import type { LocationQuery } from 'vue-router'

export const DRAWER_QUERY_KEY = 'drawer'
export const DRAWER_STEP_QUERY_KEY = 'drawerStep'

export type DrawerQueryValue =
  | 'cart'
  | 'date-request'
  | 'active-offers'
  | 'wishlist'
  | 'waitlist'

export type DrawerStepValue = 'matching-dates'

const DRAWER_QUERY_VALUES = new Set<DrawerQueryValue>([
  'cart',
  'date-request',
  'active-offers',
  'wishlist',
  'waitlist',
])

const DRAWER_STEP_VALUES = new Set<DrawerStepValue>(['matching-dates'])

export const isMobileDrawerQueryEnabled = (): boolean => {
  if (typeof window === 'undefined') return false

  try {
    return useDevice().isMobile
  } catch {
    return false
  }
}

const readQueryValue = (query: LocationQuery, key: string): string | null => {
  const raw = query[key]
  const value = Array.isArray(raw) ? raw[0] : raw
  return typeof value === 'string' ? value : null
}

export const parseDrawerQuery = (
  query: LocationQuery
): DrawerQueryValue | null => {
  const value = readQueryValue(query, DRAWER_QUERY_KEY)
  if (!value || !DRAWER_QUERY_VALUES.has(value as DrawerQueryValue)) {
    return null
  }

  return value as DrawerQueryValue
}

export const parseDrawerStepQuery = (
  query: LocationQuery
): DrawerStepValue | null => {
  const value = readQueryValue(query, DRAWER_STEP_QUERY_KEY)
  if (!value || !DRAWER_STEP_VALUES.has(value as DrawerStepValue)) {
    return null
  }

  return value as DrawerStepValue
}

export const useDrawerQueryNavigation = () => {
  const route = useRoute()
  const router = useRouter()

  const currentDrawer = computed(() => parseDrawerQuery(route.query))
  const currentDrawerStep = computed(() => parseDrawerStepQuery(route.query))

  const pushDrawerQuery = (
    drawer: DrawerQueryValue,
    step?: DrawerStepValue
  ) => {
    const { [DRAWER_STEP_QUERY_KEY]: _step, ...rest } = route.query
    const query: LocationQuery = { ...rest, [DRAWER_QUERY_KEY]: drawer }

    if (step) {
      query[DRAWER_STEP_QUERY_KEY] = step
    }

    return router.push({ query })
  }

  const popDrawerQuery = () => {
    if (parseDrawerQuery(route.query)) {
      return router.back()
    }
  }

  const clearDrawerQueryFromUrl = () => {
    if (!parseDrawerQuery(route.query)) return

    const {
      [DRAWER_QUERY_KEY]: _drawer,
      [DRAWER_STEP_QUERY_KEY]: _step,
      ...rest
    } = route.query

    return router.replace({ query: rest })
  }

  const pushDrawerStepQuery = (step: DrawerStepValue) => {
    const drawer = parseDrawerQuery(route.query)
    if (!drawer) return

    return pushDrawerQuery(drawer, step)
  }

  const popDrawerStepQuery = () => {
    if (!parseDrawerStepQuery(route.query)) return

    return router.back()
  }

  return {
    currentDrawer,
    currentDrawerStep,
    pushDrawerQuery,
    popDrawerQuery,
    clearDrawerQueryFromUrl,
    pushDrawerStepQuery,
    popDrawerStepQuery,
  }
}
